// Environment settings (spec/dashboard.md section 9.12; spec/api-conventions.md sections 15.5, 17.5, 11): the form is one
// staged `environment` draft (id = the environment slug) that Save All PATCHes with If-Match; the history usage figures;
// Run retention as a command (dry run first, then the real run after a confirmation naming the counts); game time
// disabled on production (D-24); "Last changed by … at …" from the game's audit log; the Status card from …/envs/{env}/status.
import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { components } from '../../api/schema';
import type { AuditEntry, Environment, Json } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { EmptyState } from '../../ui/Banner';
import { Field } from '../../ui/Field';
import { CommitTextInput, NumberInput, Select, Switch } from '../../ui/inputs';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { DurationInput } from '../../ui/values';
import { queryCache, useQuery } from '../../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';
import { lastUsedText } from '../settings/keys';
import { useEnvironmentsRefresh } from './useEnvironmentsRefresh';
import './env.css';
import { LoadError, LoadingPane, SkeletonRows } from '../../ui/PageStates';
import { Activity, AlertCircle, Eraser, Lock, RefreshCw, Server } from '../../ui/icons';

type EnvField = 'name' | 'isProtected' | 'authLimit' | 'presenceTimeoutSeconds' | 'historyRetentionDays' | 'historyMinVersionsPerKey' | 'analyticsRetentionDays' | 'gameTimeOffsetSeconds' | 'useLocalTime';

interface Usage {
  players: number | string;
  keys: number | string;
  versions: number | string;
  currentBytes: number | string;
  historyBytes: number | string;
  blobs: number | string;
  blobBytes: number | string;
  retentionDays: number | string;
  minVersionsPerKey: number | string;
  oldestVersionAt: string | null;
}

interface Retention {
  dryRun: boolean;
  deletedVersions: number | string;
  deletedVersionBytes: number | string;
  deletedBlobs: number | string;
  deletedBlobBytes: number | string;
  keptVersions: number | string;
  retentionDays: number | string;
  minVersionsPerKey: number | string;
}

export const PROD_GAME_TIME_NOTE = 'Production game time is UTC: test calendar content on dev or stage';

export function formatBytes(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return String(value);
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

const count = (v: number | string | null | undefined) => Number(v ?? 0).toLocaleString('en-US');

export function EnvironmentSettingsPage() {
  const { env: envSlug = '' } = useParams();
  const { org, game, environments, can, offsets } = useGame();
  const { client } = useAuth();
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const [zone] = useTimeZone();
  const listed = environments.find((e) => e.slug === envSlug);
  const path = { org, game: game.slug, env: envSlug };
  // keyed under the environment workspace's prefix: the shell's Save All invalidates `branch:<game>:env:<slug>` and these re-read
  const prefix = `branch:${game.id}:env:${envSlug}`;
  const envQ = useQuery(listed ? `${prefix}:environment` : null, () => client.data(ops.getEnvironment, { path }));
  const server = (envQ.data as Environment | undefined) ?? listed;
  const canWrite = can('manage_environment', envSlug);
  const canReadPlayers = can('players_read', envSlug);
  const usageQ = useQuery(server && canReadPlayers ? `${prefix}:history-usage` : null, () => client.data(ops.playerHistoryUsage, { path }));
  const auditQ = useQuery(server ? `${prefix}:audit:${server.id}` : null, () => client.data(ops.listGameAudit, { path: { org, game: game.slug }, query: { resourceType: 'environment', resourceId: server!.id, limit: 1 } }));
  useEnvironmentsRefresh(workspace, org, game.slug);

  if (!server) {
    return (
      <section className="env-page">
        <div className="page-head">
          <h1>Environment settings</h1>
        </div>
        {envQ.loading ? <LoadingPane rows={4} label="Loading the environment" /> : <EmptyState icon={<Server />} title={`No environment “${envSlug}”`} body="Pick an environment with the chips of the branch selector." />}
      </section>
    );
  }

  const key = entityKey('environment', server.slug);
  const draft = workspace.getDraft(key);
  const value = (draft?.current as unknown as Environment | undefined) ?? server;
  const edit = (field: EnvField, v: unknown) => {
    workspace.edit('environment', server.slug, field, v, { base: { version: Number(server.version), value: server as unknown as Json }, label: server.name, template: 'Environment' });
  };
  const isProd = server.kind === 'prod';
  const problem = draft?.conflict?.message ?? draft?.error?.message ?? null;
  const details = (draft?.error?.details ?? []).map((d) => d.message).filter(Boolean);
  const last = ((auditQ.data as { items?: AuditEntry[] } | undefined)?.items ?? [])[0];
  const offset = Number(value.gameTimeOffsetSeconds ?? 0);

  return (
    <section className="env-page">
      <div className="page-head">
        <h1>Environment settings</h1>
        <Badge tone={isProd ? 'danger' : server.kind === 'stage' ? 'warning' : 'info'}>{server.kind}</Badge>
        <span className="page-meta">
          {server.name} <code>{server.slug}</code>
        </span>
        {draft && <Badge tone="warning">Modified</Badge>}
        <span className="spacer" />
        <span className="page-sub">Saved with Save All</span>
      </div>
      <p className="muted small env-last-changed" data-testid="last-changed">
        {auditQ.loading && !auditQ.data ? 'Reading the audit log…' : last ? `Last changed by ${last.actor.label} at ${formatInstantWithZone(last.occurredAt, zone, { offsets })}` : auditQ.error ? 'The audit log is not readable with your permissions.' : 'No recorded change yet.'}
      </p>
      <StatusPanel envSlug={server.slug} canRead={can('content_read', envSlug)} />
      {problem && (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {problem}
            {details.length > 0 && (
              <ul>
                {details.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <form className="env-form" data-testid="env-settings-form" onSubmit={(e) => e.preventDefault()} aria-label="Environment settings">
        <fieldset className="pane env-section" disabled={!canWrite} title={canWrite ? undefined : "You lack the 'manage_environment' permission."}>
          <legend>General</legend>
          <div className="form-grid">
          <Field label="Name">
            <CommitTextInput value={value.name} onCommit={(v) => v.trim() !== '' && edit('name', v.trim())} aria-label="Name" />
          </Field>
          <Field label="Protected" help="A protected environment applies the deploy-time rules (who may pin and roll back); it is independent of branch protection, where changes land through merge requests.">
            <Switch label="Protected" checked={!!value.isProtected} onChange={(v) => edit('isProtected', v)} disabled={!canWrite} />
          </Field>
          <Field label="Accounts" help="With it on, linking a second binding of a type the account already holds is refused (binding_exists).">
            <Switch label="Limit one account per authorization type" checked={!!value.authLimit} onChange={(v) => edit('authLimit', v)} disabled={!canWrite} />
          </Field>
          <RangeField label="Presence timeout" unit="s" min={10} max={3600} value={Number(value.presenceTimeoutSeconds)} onCommit={(v) => edit('presenceTimeoutSeconds', v)} disabled={!canWrite} help="10–3600 seconds without a heartbeat before a session counts as gone." />
          </div>
        </fieldset>

        <fieldset className="pane env-section" disabled={!canWrite}>
          <legend>History retention</legend>
          <div className="form-grid env-row">
            <RangeField label="History retention days" unit="days" min={1} max={3650} value={Number(value.historyRetentionDays)} onCommit={(v) => edit('historyRetentionDays', v)} disabled={!canWrite} help="1–3650: versions older than this may be deleted…" />
            <RangeField label="Minimum versions per key" unit="versions" min={0} max={1000} value={Number(value.historyMinVersionsPerKey)} onCommit={(v) => edit('historyMinVersionsPerKey', v)} disabled={!canWrite} help="0–1000: …unless they are among the newest of their key (the newest one is always kept)." />
          </div>
          <HistoryUsage usage={usageQ.data as Usage | undefined} loading={usageQ.loading} error={usageQ.error} canRead={canReadPlayers} />
        </fieldset>

        <fieldset className="pane env-section" disabled={!canWrite}>
          <legend>Analytics retention</legend>
          <div className="form-grid env-row">
            <RangeField label="Analytics retention days" unit="days" min={1} max={3650} value={Number(value.analyticsRetentionDays ?? 400)} onCommit={(v) => edit('analyticsRetentionDays', v)} disabled={!canWrite} help="1–3650: raw analytics events older than this (by their own time) are deleted by the nightly sweep; the day counters already folded from them stay (spec/api-conventions.md §21.6)." />
          </div>
        </fieldset>
      </form>

      <RetentionPanel envSlug={server.slug} canRun={can('players_write', envSlug)} staged={!!draft?.changes.some((c) => c.path === 'historyRetentionDays' || c.path === 'historyMinVersionsPerKey')} onDone={() => queryCache.invalidate(`${prefix}:history-usage`)} />

      <form className="env-form" onSubmit={(e) => e.preventDefault()} aria-label="Game time">
        <fieldset className="pane env-section" disabled={!canWrite || isProd}>
          <legend>Game time</legend>
          {isProd && (
            <div className="banner banner-warning" data-testid="game-time-prod-note" role="note">
              <Lock className="banner-icon" aria-hidden="true" />
              <div className="banner-body">{PROD_GAME_TIME_NOTE}</div>
            </div>
          )}
          <div className="form-grid">
          <Field label="Offset" help="Shifts the environment's game clock by up to one day either way (the dev / stage display zones of the clock follow it).">
            <SignedDuration value={offset} onCommit={(v) => edit('gameTimeOffsetSeconds', v)} disabled={!canWrite || isProd} />
          </Field>
          <Field label="Use local time" help="Calendar content follows the player's local time instead of game time.">
            <Switch label="Use local time" checked={!!value.useLocalTime} onChange={(v) => edit('useLocalTime', v)} disabled={!canWrite || isProd} />
          </Field>
          </div>
        </fieldset>
      </form>
    </section>
  );
}

function RangeField({ label, unit, min, max, value, onCommit, disabled, help }: { label: string; unit: string; min: number; max: number; value: number; onCommit: (v: number) => void; disabled?: boolean; help?: ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <Field label={`${label} (${unit})`} help={help} error={error}>
      <NumberInput
        kind="int"
        value={value}
        aria-label={label}
        disabled={disabled}
        onCommit={(v) => {
          const n = Number(v);
          if (v === null || !Number.isInteger(n) || n < min || n > max) {
            setError(`Between ${min} and ${max}.`);
            return;
          }
          setError(null);
          onCommit(n);
        }}
      />
    </Field>
  );
}

/** The game-time offset: a sign and a `DurationInput` of its magnitude, at most one day (±86400 s). */
function SignedDuration({ value, onCommit, disabled }: { value: number; onCommit: (v: number) => void; disabled?: boolean }) {
  const [error, setError] = useState<string | null>(null);
  const sign = value < 0 ? -1 : 1;
  const commit = (s: number, magnitude: number) => {
    if (magnitude > 86400) {
      setError('At most one day (86400 s) either way.');
      return;
    }
    setError(null);
    const next = s * magnitude;
    if (next !== value) onCommit(next);
  };
  return (
    <span className="stack">
      <span className="row">
        <Select size="s" aria-label="Offset sign" value={sign < 0 ? '-' : '+'} disabled={disabled} onChange={(e) => commit(e.target.value === '-' ? -1 : 1, Math.abs(value))}>
          <option value="+">+ ahead of UTC</option>
          <option value="-">− behind UTC</option>
        </Select>
        <DurationInput value={Math.abs(value)} onCommit={(s) => commit(sign, s)} zeroMeans="none" disabled={disabled} aria-label="Game time offset" />
      </span>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function HistoryUsage({ usage, loading, error, canRead }: { usage: Usage | undefined; loading: boolean; error: unknown; canRead: boolean }) {
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  return (
    <div className="history-usage" data-testid="history-usage">
      <h3>Usage</h3>
      {!canRead ? (
        <p className="muted small">The usage figures need the 'players_read' permission on this environment.</p>
      ) : loading && !usage ? (
        <SkeletonRows rows={2} label="Loading the usage" />
      ) : error ? (
        <LoadError>{error instanceof ApiError ? error.message : String(error)}</LoadError>
      ) : usage ? (
        <dl className="kv usage-figures">
          <dt>Players</dt>
          <dd>{count(usage.players)}</dd>
          <dt>Keys</dt>
          <dd>{count(usage.keys)}</dd>
          <dt>Versions</dt>
          <dd>{count(usage.versions)}</dd>
          <dt>Current bytes</dt>
          <dd>{formatBytes(usage.currentBytes)}</dd>
          <dt>History bytes</dt>
          <dd>{formatBytes(usage.historyBytes)}</dd>
          <dt>Oldest version</dt>
          <dd>{usage.oldestVersionAt ? formatInstantWithZone(usage.oldestVersionAt, zone, { offsets }) : 'none'}</dd>
          <dt>Applied settings</dt>
          <dd>
            {count(usage.retentionDays)} days · {count(usage.minVersionsPerKey)} versions per key
          </dd>
        </dl>
      ) : null}
    </div>
  );
}

function retentionCounts(r: Retention, dry: boolean): string {
  const verb = dry ? 'would delete' : 'deleted';
  return `${verb} ${count(r.deletedVersions)} version${Number(r.deletedVersions) === 1 ? '' : 's'} (${formatBytes(r.deletedVersionBytes)}) and ${count(r.deletedBlobs)} blob${Number(r.deletedBlobs) === 1 ? '' : 's'} (${formatBytes(r.deletedBlobBytes)}); ${count(r.keptVersions)} version${Number(r.keptVersions) === 1 ? '' : 's'} ${dry ? 'would remain' : 'remain'}`;
}

/** Run retention (section 17.5): a command, never staged — a dry run first, then the real run after a confirmation naming its counts. */
function RetentionPanel({ envSlug, canRun, staged, onDone }: { envSlug: string; canRun: boolean; staged: boolean; onDone: () => void }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const confirm = useConfirm();
  const toasts = useToasts();
  const [dry, setDry] = useState<Retention | null>(null);
  const [result, setResult] = useState<Retention | null>(null);
  const [busy, setBusy] = useState<'dry' | 'run' | null>(null);
  const path = { org, game: game.slug, env: envSlug };
  const run = async (dryRun: boolean) => {
    setBusy(dryRun ? 'dry' : 'run');
    try {
      const r = (await client.data(ops.runPlayerHistoryRetention, { path, body: { dryRun } })) as unknown as Retention;
      if (dryRun) {
        setDry(r);
        setResult(null);
      } else {
        setResult(r);
        setDry(null);
        toasts.success(`Retention ran: ${retentionCounts(r, false)}.`);
        onDone();
      }
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
    }
  };
  const runForReal = async () => {
    if (!dry) return;
    const ok = await confirm({
      title: 'Run retention now?',
      body: `This deletes ${count(dry.deletedVersions)} version${Number(dry.deletedVersions) === 1 ? '' : 's'} (${formatBytes(dry.deletedVersionBytes)}) and ${count(dry.deletedBlobs)} blob${Number(dry.deletedBlobs) === 1 ? '' : 's'} (${formatBytes(dry.deletedBlobBytes)}); ${count(dry.keptVersions)} versions remain. It cannot be undone.`,
      confirmLabel: 'Run retention',
      tone: 'danger',
    });
    if (ok) await run(false);
  };
  const missing = canRun ? null : 'players_write';
  return (
    <section className="pane env-panel" data-testid="retention-panel" aria-label="Run retention">
      <div className="pane-head">
        <Eraser className="icon" aria-hidden="true" />
        <h2>Run retention</h2>
      </div>
      <div className="pane-body">
      <p className="muted small">
        Deletes the player-save versions that are both older than the retention days and not among the newest of their key, with the settings saved on the server (the nightly job does the same).
        {staged ? ' Your staged retention changes apply only after Save All.' : ''}
      </p>
      {dry && (
        <p className="small" data-testid="retention-dry-result">
          Dry run ({count(dry.retentionDays)} days, {count(dry.minVersionsPerKey)} versions per key): {retentionCounts(dry, true)}.
        </p>
      )}
      {result && (
        <p className="small" data-testid="retention-result">
          Done: {retentionCounts(result, false)}.
        </p>
      )}
      </div>
      <div className="pane-foot">
        <Button onClick={() => void run(true)} busy={busy === 'dry'} missingPermission={missing}>
          Dry run
        </Button>
        <Button variant="danger" onClick={() => void runForReal()} busy={busy === 'run'} disabled={!dry} missingPermission={missing} title={dry ? undefined : 'Run a dry run first'}>
          Run
        </Button>
      </div>
    </section>
  );
}

type EnvironmentStatus = components['schemas']['EnvironmentStatusDto'];

/** A relative time ("3h ago", "just now") with the instant in the display zone on hover. */
function Ago({ iso, now }: { iso: string; now: number }) {
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  return <span title={formatInstantWithZone(iso, zone, { offsets })}>{lastUsedText(iso, now)}</span>;
}

/** Joins the parts of one status line with " · ". */
function parts(items: (ReactNode | false | null | undefined)[]): ReactNode[] {
  const kept = items.filter((x) => x !== false && x !== null && x !== undefined);
  return kept.flatMap((x, i) => (i === 0 ? [x] : [<span key={`sep${i}`}> · </span>, x]));
}

/**
 * The environment's status (spec/dashboard.md section 9.12; spec/api-conventions.md section 21.8): one read of
 * `GET …/envs/{env}/status` on mount and on Refresh (no polling) — what the pointer serves, ingestion and the fold, the jobs,
 * the game's webhook deliveries, the last retention run. A 403 (no `content_read`) is a muted line, not an error.
 */
function StatusPanel({ envSlug, canRead }: { envSlug: string; canRead: boolean }) {
  const { org, game, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<{ data: EnvironmentStatus | null; error: unknown; loading: boolean }>({ data: null, error: null, loading: canRead });
  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    client.data(ops.getEnvironmentStatus, { path: { org, game: game.slug, env: envSlug } }).then(
      (data) => {
        if (!cancelled) setState({ data: data as EnvironmentStatus, error: null, loading: false });
      },
      (error: unknown) => {
        if (!cancelled) setState((s) => ({ data: s.data, error, loading: false }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client, org, game.slug, envSlug, canRead, tick]);

  const denied = !canRead || (state.error instanceof ApiError && state.error.status === 403);
  const s = state.data;
  const now = Date.now();
  let body: ReactNode = null;
  if (denied) {
    body = (
      <p className="muted small" data-testid="status-permission">
        Status needs the content_read permission
      </p>
    );
  } else if (state.loading && !s) {
    body = <SkeletonRows rows={3} label="Reading status" />;
  } else if (state.error && !s) {
    body = (
      <LoadError>{state.error instanceof ApiError ? state.error.message : String(state.error)}</LoadError>
    );
  } else if (s) {
    const ing = s.ingestion;
    const backlog = Number(ing.backlogRows);
    const lag = Math.round(Number(ing.lagSeconds ?? 0));
    const backlogText = `${count(backlog)} rows waiting, lag ${count(lag)} s`;
    const pastLease = Number(s.jobs.pastLease);
    const pending = Number(s.webhooks.pending);
    const failed = Number(s.webhooks.failed);
    const ret = s.retention;
    const last = s.jobs.lastFinished;
    body = (
      <dl className="kv usage-figures">
        <dt>Serving</dt>
        <dd data-testid="status-serving">
          {parts([
            <span key="pin">
              {s.pointer.releaseId && <code>{s.pointer.releaseId}</code>}
              {s.pointer.releaseId && ' '}
              {s.pin ? (
                <>
                  pinned {s.pin.createdAt && <Ago iso={s.pin.createdAt} now={now} />} by {s.pin.pinnedBy.label}
                </>
              ) : (
                'nothing pinned'
              )}
            </span>,
            Number(s.pointer.ranges) > 0 && <span key="ranges">{count(s.pointer.ranges)} app-version ranges</span>,
            <span key="stored">
              {s.pointer.stored ? (
                <>
                  pointer stored {s.pointer.updatedAt && <Ago iso={s.pointer.updatedAt} now={now} />}
                </>
              ) : (
                'composed from the pins'
              )}
            </span>,
            s.release && (
              <span key="release">
                release #{count(s.release.number)} <code>{s.release.commit.slice(0, 7)}</code>
              </span>
            ),
          ])}
        </dd>
        <dt>Ingestion</dt>
        <dd data-testid="status-ingestion">
          {parts([
            <span key="received">
              {ing.lastReceivedAt ? (
                <>
                  last received <Ago iso={ing.lastReceivedAt} now={now} />
                </>
              ) : (
                'no events yet'
              )}
            </span>,
            backlog > 0 &&
              (lag > 300 ? (
                <Badge key="backlog" tone="warning">
                  {backlogText}
                </Badge>
              ) : (
                <span key="backlog">{backlogText}</span>
              )),
            <span key="fold">
              {ing.foldWatermark ? (
                <>
                  folded through <Ago iso={ing.foldWatermark} now={now} />, {count(ing.foldedRows)} rows
                </>
              ) : (
                'never folded'
              )}
            </span>,
          ])}
        </dd>
        <dt>Jobs</dt>
        <dd data-testid="status-jobs">
          {parts([
            <span key="queued">queued {count(s.jobs.queued)}</span>,
            <span key="running">running {count(s.jobs.running)}</span>,
            pastLease > 0 ? (
              <Badge key="lease" tone="danger">
                past lease {count(pastLease)}
              </Badge>
            ) : (
              <span key="lease">past lease 0</span>
            ),
            last && (
              <span key="last">
                last finished: {last.kind} {last.status} {last.finishedAt && <Ago iso={last.finishedAt} now={now} />}
              </span>
            ),
          ])}
        </dd>
        <dt>Webhooks</dt>
        <dd data-testid="status-webhooks">
          {pending === 0 && failed === 0
            ? 'no webhooks in flight'
            : parts([
                <span key="pending">pending {count(pending)}</span>,
                failed > 0 ? (
                  <Badge key="failed" tone="danger">
                    failed {count(failed)}
                  </Badge>
                ) : (
                  <span key="failed">failed 0</span>
                ),
              ])}
        </dd>
        <dt>Retention</dt>
        <dd data-testid="status-retention">
          {ret.lastRunAt ? (
            <>
              last run <Ago iso={ret.lastRunAt} now={now} />: {count(ret.deletedVersions)} versions ({formatBytes(ret.deletedBytes)}) deleted, {count(ret.keptVersions)} kept
            </>
          ) : (
            'never run'
          )}
        </dd>
        {s.legacyImport && (
          <>
            <dt>Lazy import</dt>
            <dd data-testid="status-legacy-import">
              {parts([
                s.legacyImport.enabled ? <span key="on">on</span> : <Badge key="on">off</Badge>,
                <span key="imported">imported {count(s.legacyImport.counts.imported)}</span>,
                <span key="new">new players {count(s.legacyImport.counts.miss)}</span>,
                Number(s.legacyImport.counts.tokenRefused) > 0 && <span key="refused">token refused {count(s.legacyImport.counts.tokenRefused)}</span>,
                Number(s.legacyImport.counts.failed) > 0 && (
                  <Badge key="failed" tone="warning">
                    failed {count(s.legacyImport.counts.failed)}
                  </Badge>
                ),
                s.legacyImport.counts.lastImportAt && (
                  <span key="last">
                    last import <Ago iso={s.legacyImport.counts.lastImportAt} now={now} />
                  </span>
                ),
              ])}
            </dd>
          </>
        )}
      </dl>
    );
  }
  return (
    <section className="pane env-panel" data-testid="status-panel" aria-label="Status">
      <div className="pane-head">
        <Activity className="icon" aria-hidden="true" />
        <h2>Status</h2>
        <span className="spacer" />
        {!denied && s?.computedAt && (
          <span className="muted small" data-testid="status-as-of">
            as of {formatInstantWithZone(s.computedAt, zone, { offsets })}
          </span>
        )}
        {!denied && (
          <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={() => setTick((t) => t + 1)} busy={state.loading}>
            Refresh
          </Button>
        )}
      </div>
      <div className="pane-body">{body}</div>
    </section>
  );
}
